<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import { paymentMethodsApi } from '../stores/api.js';
  import { activeCompanyId } from '../stores/company.js';
  import { canWriteInCompany } from '../stores/capabilities.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { Banknote, Plus, Pencil, Trash2, Star, Check, X as XIcon, Save } from '../lib/icons/index.js';

  // When `embedded` is true the page renders only its content (no Layout
  // wrapper, no page header) so it can be slotted into the Settings page as a
  // tab body. When false it behaves as a standalone page — kept for
  // backward-compatible deep links and tests.
  export let embedded = false;

  let methods = [];
  let loading = true;
  let error = '';
  let showForm = false;
  let editingId = null;
  let form = emptyForm();

  function emptyForm() {
    return {
      label: '',
      modalitaPagamento: 'MP05',
      condizioni: 'TP02',
      scadenzaGiorni: 30,
      iban: '',
      abi: '',
      cab: '',
      bic: '',
      istitutoFinanziario: '',
      beneficiario: '',
      note: '',
      isDefault: false,
    };
  }

  // Common FatturaPA modalità codes shown in the dropdown with human labels.
  const modalitaOptions = [
    { code: 'MP01', label: 'Contanti' },
    { code: 'MP02', label: 'Assegno' },
    { code: 'MP03', label: 'Assegno circolare' },
    { code: 'MP04', label: 'Contanti presso Tesoreria' },
    { code: 'MP05', label: 'Bonifico' },
    { code: 'MP06', label: 'Vaglia cambiario' },
    { code: 'MP07', label: 'Bollettino bancario' },
    { code: 'MP08', label: 'Carta di pagamento' },
    { code: 'MP09', label: 'RID' },
    { code: 'MP10', label: 'RID utenze' },
    { code: 'MP11', label: 'RID veloce' },
    { code: 'MP12', label: 'RIBA' },
    { code: 'MP13', label: 'MAV' },
    { code: 'MP14', label: 'Quietanza erario' },
    { code: 'MP15', label: 'Giroconto su conti di contabilità speciale' },
    { code: 'MP16', label: 'Domiciliazione bancaria' },
    { code: 'MP17', label: 'Domiciliazione postale' },
    { code: 'MP18', label: 'Bollettino di c/c postale' },
    { code: 'MP19', label: 'SEPA Direct Debit' },
    { code: 'MP20', label: 'SEPA Direct Debit CORE' },
    { code: 'MP21', label: 'SEPA Direct Debit B2B' },
    { code: 'MP22', label: 'Trattenuta su somme già riscosse' },
    { code: 'MP23', label: 'PagoPA' },
  ];

  const condizioniOptions = [
    { code: 'TP01', label: tImmediate('paymentMethods.condizioni.TP01', {}, 'Pagamento a rate') },
    { code: 'TP02', label: tImmediate('paymentMethods.condizioni.TP02', {}, 'Pagamento completo') },
    { code: 'TP03', label: tImmediate('paymentMethods.condizioni.TP03', {}, 'Anticipo') },
  ];

  $: companyId = $activeCompanyId;

  onMount(() => { if (companyId) load(); });
  $: if (companyId) load();

  async function load() {
    loading = true;
    error = '';
    try {
      methods = await paymentMethodsApi.list(companyId, { includeInactive: false });
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  }

  function openCreate() {
    form = emptyForm();
    editingId = null;
    showForm = true;
  }

  function openEdit(m) {
    editingId = m.id;
    form = {
      label: m.label || '',
      modalitaPagamento: m.modalitaPagamento || 'MP05',
      condizioni: m.condizioni || 'TP02',
      scadenzaGiorni: m.scadenzaGiorni != null ? m.scadenzaGiorni : 30,
      iban: m.iban || '',
      abi: m.abi || '',
      cab: m.cab || '',
      bic: m.bic || '',
      istitutoFinanziario: m.istitutoFinanziario || '',
      beneficiario: m.beneficiario || '',
      note: m.note || '',
      isDefault: !!m.isDefault,
    };
    showForm = true;
  }

  function cancelForm() {
    showForm = false;
    editingId = null;
  }

  async function submit(e) {
    e.preventDefault();
    error = '';
    try {
      if (editingId) {
        await paymentMethodsApi.update(companyId, editingId, form);
      } else {
        await paymentMethodsApi.create(companyId, form);
      }
      await load();
      cancelForm();
    } catch (err) {
      error = err.message;
    }
  }

  async function handleDelete(m) {
    if (!confirm(tImmediate('paymentMethods.confirmDelete'))) return;
    try {
      await paymentMethodsApi.remove(companyId, m.id);
      await load();
    } catch (err) {
      error = err.message;
    }
  }

  async function makeDefault(m) {
    try {
      await paymentMethodsApi.update(companyId, m.id, { isDefault: true });
      await load();
    } catch (err) {
      error = err.message;
    }
  }

  function modalitaLabel(code) {
    const match = modalitaOptions.find(o => o.code === code);
    return match ? `${match.code} · ${match.label}` : code;
  }

  function condizioniLabel(code) {
    return code ? tImmediate(`paymentMethods.condizioni.${code}`, undefined, code) : '-';
  }
</script>

{#snippet body()}
  <div class="page-header flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
    <div>
      {#if !embedded}
        <div class="flex items-center gap-2">
          <Banknote size={22} class="text-action" />
          <h1 class="section-title mb-0">{$t('paymentMethods.title')}</h1>
        </div>
      {/if}
      <p class="text-muted text-sm mt-1 max-w-2xl">{$t('paymentMethods.description')}</p>
    </div>
    {#if $canWriteInCompany}
      <button class="btn btn-accent btn-sm" on:click={openCreate}>
        <Plus size={14} class="mr-1" /> {$t('paymentMethods.add')}
      </button>
    {/if}
  </div>

  {#if !companyId}
    <div class="card p-8 text-center text-muted">{$t('paymentMethods.selectCompanyFirst')}</div>
  {:else}
    <ErrorBanner {error} />

    {#if showForm}
      <div class="card p-5 mb-4">
        <div class="flex items-center justify-between mb-4">
          <h2 class="eyebrow eyebrow-lg" style="color: var(--color-primary);">
            {editingId ? $t('paymentMethods.form.editTitle') : $t('paymentMethods.form.createTitle')}
          </h2>
          <button class="text-muted hover:text-action-strong" on:click={cancelForm}><XIcon size={18} /></button>
        </div>
        <form on:submit={submit} class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="flex flex-col md:col-span-2">
            <span class="eyebrow text-muted mb-1">{$t('paymentMethods.fields.label')} *</span>
            <input class="input" required maxlength="128" bind:value={form.label} placeholder={$t('paymentMethods.form.labelPlaceholder')} />
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.modalita')} *<InfoHint text={$t('paymentMethods.fieldHints.modalita')} /></span>
            <select class="input" required bind:value={form.modalitaPagamento}>
              {#each modalitaOptions as opt}
                <option value={opt.code}>{opt.code} — {opt.label}</option>
              {/each}
            </select>
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.condizioni')}<InfoHint text={$t('paymentMethods.fieldHints.condizioni')} /></span>
            <select class="input" bind:value={form.condizioni}>
              {#each condizioniOptions as opt}
                <option value={opt.code}>{opt.code} — {opt.label}</option>
              {/each}
            </select>
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.scadenzaGiorni')}<InfoHint text={$t('paymentMethods.fieldHints.scadenzaGiorni')} /></span>
            <input class="input" type="number" min="0" bind:value={form.scadenzaGiorni} />
          </label>
          <label class="flex flex-col md:col-span-2">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.iban')}<InfoHint text={$t('paymentMethods.fieldHints.iban')} /></span>
            <input class="input font-mono" maxlength="34" bind:value={form.iban} placeholder="IT60X..." />
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.abi')}<InfoHint text={$t('paymentMethods.fieldHints.abi')} /></span>
            <input class="input font-mono" maxlength="8" bind:value={form.abi} />
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.cab')}<InfoHint text={$t('paymentMethods.fieldHints.cab')} /></span>
            <input class="input font-mono" maxlength="8" bind:value={form.cab} />
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.bic')}<InfoHint text={$t('paymentMethods.fieldHints.bic')} /></span>
            <input class="input font-mono" maxlength="16" bind:value={form.bic} />
          </label>
          <label class="flex flex-col">
            <span class="eyebrow text-muted mb-1">{$t('paymentMethods.fields.istituto')}</span>
            <input class="input" maxlength="256" bind:value={form.istitutoFinanziario} />
          </label>
          <label class="flex flex-col md:col-span-2">
            <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('paymentMethods.fields.beneficiario')}<InfoHint text={$t('paymentMethods.fieldHints.beneficiario')} /></span>
            <input class="input" maxlength="256" bind:value={form.beneficiario} placeholder={$t('paymentMethods.form.beneficiarioPlaceholder')} />
          </label>
          <label class="flex flex-col md:col-span-2">
            <span class="eyebrow text-muted mb-1">{$t('paymentMethods.fields.note')}</span>
            <textarea class="input" rows="2" bind:value={form.note}></textarea>
          </label>
          <label class="flex items-center gap-2 md:col-span-2">
            <input type="checkbox" bind:checked={form.isDefault} />
            <span class="text-sm">{$t('paymentMethods.fields.isDefault')}</span>
          </label>
          <div class="md:col-span-2 flex justify-end gap-2">
            <button type="button" class="btn btn-secondary btn-sm" on:click={cancelForm}>{$t('common.cancel')}</button>
            <button type="submit" class="btn btn-accent btn-sm"><Save size={14} class="mr-1" /> {$t('common.save')}</button>
          </div>
        </form>
      </div>
    {/if}

    {#if loading}
      <PageLoader />
    {:else if methods.length === 0}
      <div class="card p-8 text-center text-muted">{$t('paymentMethods.empty')}</div>
    {:else}
      <div class="card p-0 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="table-modern">
            <thead class="table-header">
              <tr>
                <th class="table-cell table-header-cell text-left">{$t('paymentMethods.fields.label')}</th>
                <th class="table-cell table-header-cell text-left">{$t('paymentMethods.fields.modalita')}</th>
                <th class="table-cell table-header-cell text-left">{$t('paymentMethods.fields.condizioni')}</th>
                <th class="table-cell table-header-cell text-right">{$t('paymentMethods.fields.scadenzaGiorni')}</th>
                <th class="table-cell table-header-cell text-left">{$t('paymentMethods.fields.iban')}</th>
                <th class="table-cell table-header-cell text-right"></th>
              </tr>
            </thead>
            <tbody>
              {#each methods as m}
                <tr class="table-row" style="cursor: default;">
                  <td class="table-cell">
                    <div class="flex items-center gap-2">
                      <span class="font-bold text-heading">{m.label}</span>
                      {#if m.isDefault}
                        <span class="badge badge-cyan inline-flex items-center gap-1"><Star size={10} /> {$t('paymentMethods.defaultBadge')}</span>
                      {/if}
                    </div>
                    {#if m.note}<p class="text-xs text-muted mt-1">{m.note}</p>{/if}
                  </td>
                  <td class="table-cell text-xs font-mono">{modalitaLabel(m.modalitaPagamento)}</td>
                  <td class="table-cell text-xs">{condizioniLabel(m.condizioni)}</td>
                  <td class="table-cell text-xs font-mono text-right">{m.scadenzaGiorni != null ? `${m.scadenzaGiorni} gg` : '-'}</td>
                  <td class="table-cell text-xs font-mono break-all">{m.iban || '-'}</td>
                  <td class="table-cell text-right">
                    <div class="flex items-center gap-1 justify-end">
                      {#if $canWriteInCompany && !m.isDefault}
                        <button class="p-1 hover:text-action-strong" title={$t('paymentMethods.setDefault')} on:click={() => makeDefault(m)}>
                          <Star size={16} />
                        </button>
                      {/if}
                      {#if $canWriteInCompany}
                        <button class="p-1 hover:text-action-strong" title={$t('common.edit')} on:click={() => openEdit(m)}>
                          <Pencil size={16} />
                        </button>
                        <button class="p-1 hover:text-danger" title={$t('common.delete')} on:click={() => handleDelete(m)}>
                          <Trash2 size={16} />
                        </button>
                      {/if}
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  {/if}
{/snippet}

{#if embedded}
  {@render body()}
{:else}
  <Layout>
    {@render body()}
  </Layout>
{/if}
